/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import {
  Dispatch,
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'

import { Nullable } from '../types/main'

export type ModalConfig = {
  showCloseButton: boolean
  showDialogHeader: boolean
  showDialogFooter: boolean
  dialogTitle: string
  fullscreen: boolean
  asWidgetContainer: boolean
}

const INITIAL_MODAL_CONFIG: ModalConfig = {
  showCloseButton: true,
  showDialogHeader: true,
  showDialogFooter: true,
  dialogTitle: '',
  fullscreen: false,
  asWidgetContainer: false
}

export type ModalContextType = {
  modalIsOpen: boolean
  modalConfig: ModalConfig
  openedModalStack: string[]
  ModalViewProxy: FunctionComponent<{}>
  openModal: (view: ReactNode, key: string, config: Partial<ModalConfig>) => void
  closeModal: (key?: string) => void
  setModalView: Dispatch<SetStateAction<Nullable<ReactNode>>>
  setModalConfig: Dispatch<SetStateAction<ModalConfig>>
}

const INITIAL_MODAL_CONTEXT: ModalContextType = {
  modalIsOpen: false,
  modalConfig: INITIAL_MODAL_CONFIG,
  openedModalStack: [],
  ModalViewProxy: () => {
    throw new Error('ModalViewProxy function component must be overriden before use')
  },
  openModal: () => {
    throw new Error('openModal function must be overriden before use')
  },
  closeModal: () => {
    throw new Error('closeModal function must be overriden before use')
  },
  setModalView: () => {
    throw new Error('setModalView function must be overriden before use')
  },
  setModalConfig: () => {
    throw new Error('setModalConfig function must be overriden before use')
  }
}

const ModalContext = createContext(INITIAL_MODAL_CONTEXT)

const ModalContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [modalConfig, setModalConfig] = useState<ModalConfig>(INITIAL_MODAL_CONFIG)
  const [modalView, setModalView] = useState<Nullable<ReactNode>>(null)
  const [openedModalStack, setOpenedModalStack] = useState<string[]>([])

  const openModal = useCallback(
    (view: ReactNode, key: string, config: Partial<ModalConfig> = {}) => {
      if (!openedModalStack.includes(key)) {
        setOpenedModalStack((prev) => [...prev, key])
      }

      setModalView(view)
      setModalConfig((initConfig) => ({
        ...initConfig,
        ...config
      }))
      setModalIsOpen(true)
    },
    [openedModalStack]
  )

  const closeModal = useCallback(
    (key?: string) => {
      if (key && openedModalStack.includes(key)) {
        const stack = [...openedModalStack]
        stack.splice(stack.indexOf(key), 1)
        setOpenedModalStack(stack)
      } else {
        setOpenedModalStack([])
      }

      setModalView(null)
      setModalConfig(INITIAL_MODAL_CONFIG)
      setModalIsOpen(false)
    },
    [openedModalStack]
  )

  const ModalViewProxy = useMemo(() => () => <>{modalView}</>, [modalView])

  return (
    <ModalContext.Provider
      value={{
        modalIsOpen,
        modalConfig,
        openedModalStack,
        ModalViewProxy,
        openModal,
        closeModal,
        setModalConfig,
        setModalView
      }}
      children={children}
    />
  )
}

export default ModalContextProvider

export const useModalContext = () => useContext(ModalContext)
